import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { cloneDeep, orderBy, uniq } from 'lodash';
import Datepicker from 'react-datepicker';
import PropTypes, { any } from 'prop-types';
import Modal from '../../components/Modal/ReactBootstrapModal';

import MatchV2 from './MatchV2';
import Img from '../../components/Img';
import { getRoundSpacerClassName, getTimeSelectOptions, isSinglesFormat, matchPositionNumbers3r, matchPositionNumbers5r } from '../../utils/events';
import { drawScheduleTypeIds, drawEliminationTypeIds, drawPermissionTypeIds } from '../../utils/eventConstants';
import { renderRoundLabel } from '../../utils/draws';
import * as drawEditActions from '../../actions/draw-edit-actions';

const _ = { cloneDeep, orderBy, uniq };

const RoundsV2 = (props) => {
    const { isReadOnly, draw, parentDraw, selectedRound, updateSelectedRound, disableNextButton, playersDict, playersDictByMemberId, timeZone, utrType, teamsDict, isCutOffRound, addToMatch, handlePostScore, isPTTEvent, membersInDrawDict, teamsInDrawDict, getAvailablePlayersForDraw, getAvailableTeamsForDraw, sportTypeId } = props;

    const { locations, event } = useSelector(({ drawEdit }) => {
        const { locations, event } = drawEdit;
        return { locations, event };
    });
    const dispatch = useDispatch();

    const { scheduleTypeId } = event.data || {};

    const isPlayerPostDraw = draw?.eventDrawPermissionTypeId === drawPermissionTypeIds.PLAYERPOST;

    const { eventStartUtc, eventEndUtc } = event.data?.eventSchedule || {};
    const eventTimeZone = timeZone?.value;
    const eventStartLocal = eventTimeZone ? moment.utc(eventStartUtc).tz(eventTimeZone) : moment.utc(eventStartUtc);
    const eventEndLocal = eventTimeZone ? moment.utc(eventEndUtc).tz(eventTimeZone) : moment.utc(eventEndUtc);

    // PTT round robin position numbers
    const isRoundRobin = draw.eliminationTypeId === 3;
    let matchPositionNumbers;
    if (isPTTEvent && isRoundRobin && (draw.rounds.length === 3 || draw.rounds.length === 5)) {
        matchPositionNumbers = draw.rounds.length === 3 ? matchPositionNumbers3r : matchPositionNumbers5r;
    }

    const [showModal, setShowModal] = useState(false);
    const [location, setLocation] = useState('');
    const [facilityId, setFacilityId] = useState('');
    const [courtId, setCourtId] = useState('');
    const [selectedRoundNumber, setSelectedRoundNumber] = useState();
    const [selectedRoundMoment, setSelectedRoundMoment] = useState();
    const [timeSelect, setTimeSelect] = useState('');
    const [propagate, setPropagate] = useState(false);

    const showRoundModal = (roundNumber, roundMoment, loc) => {
        dispatch(drawEditActions.setResetTimer(true));
        setSelectedRoundNumber(roundNumber);
        setSelectedRoundMoment(roundMoment);
        setLocation(loc || '');
        setShowModal(true);
        setTimeSelect('');
    };

    const closeModals = () => {
        dispatch(drawEditActions.setResetTimer(true));
        setPropagate(false);
        setShowModal(false);
    };

    const handleRoundDateChange = (value) => {
        dispatch(drawEditActions.setResetTimer(true));
        const time = timeSelect?.split(':') || [0, 0];
        const date = value.clone();
        date.set({ hour: time[0], minute: time[1], second: 0 });
        setSelectedRoundMoment(date);
    };

    const handleRoundTimeChange = (timeVal) => {
        dispatch(drawEditActions.setResetTimer(true));
        const time = timeVal?.split(':') || [0, 0];
        const date = selectedRoundMoment.clone();
        date.set({ hour: time[0], minute: time[1], second: 0 });
        setSelectedRoundMoment(date);
        setTimeSelect(timeVal);
    };

    const handleRoundLocationChange = (e) => {
        dispatch(drawEditActions.setResetTimer(true));
        setLocation(e.target.value);
    };

    const updateRoundDateTime = () => {
        dispatch(drawEditActions.setResetTimer(true));
        const newDate = moment(selectedRoundMoment).utc().format('YYYY-MM-DDTHH:mm:ss');

        if (scheduleTypeId === drawScheduleTypeIds.MATCH_SHEET) {
            const roundId = draw.rounds[selectedRoundNumber - 1].id;
            dispatch(drawEditActions.updateMatchSheetRound({ roundId,
                payload: {
                    date: newDate,
                    facilityId: (facilityId === '' ? null : facilityId),
                    courtId: (courtId === '' ? null : courtId),
                } }));
        }
        setShowModal(false);
    };

    const toggleRoundPropagation = () => {
        setPropagate(!propagate);
    };

    const getDateMoment = (date) => {
        if (event.data?.eventSchedule.timeZone) {
            return date ? moment.utc(date).tz(event.data.eventSchedule.timeZone?.value) : moment.utc(event.data.eventSchedule.eventStartUtc).tz(event.data.eventSchedule.timeZone?.value).set({ hour: 8, minute: 0 });
        }

        if (event.data?.eventSchedule.eventStartUtc) {
            return date ? moment.utc(date) : moment.utc(event.data.eventSchedule.eventStartUtc).set({ hour: 8, minute: 0 });
        }

        return null;
    };

    const roundDetails = (draw, selectedRound, offset, testIdPrefix) => {
        const date = draw.rounds?.[selectedRound]?.date || null;
        const location = draw.rounds?.[selectedRound]?.detail || null;
        const dateMoment = getDateMoment(date);
        const selfPost = draw.eventDrawPermissionTypeId === drawPermissionTypeIds.SELFONLY;
        const showRoundAutoFill = draw.eliminationTypeId === drawEliminationTypeIds.AD_HOC;

        return (
            <div className={`d-flex`}>
                {scheduleTypeId === drawScheduleTypeIds.MATCH_SHEET ? (
                    <div className="flex-grow name ellipsis">
                        <p className={`location mt16 ${showRoundAutoFill ? 'left-align' : ''}`} data-testid={testIdPrefix && `${testIdPrefix}.location`}>
                            {isReadOnly ? location || <>&nbsp;</> : location ? (
                                <u className="cursor-pointer" onClick={() => !selfPost && showRoundModal((selectedRound + offset), dateMoment, location)} data-testid={testIdPrefix && `${testIdPrefix}.set-location-btn`}>
                                    {location}
                                </u>
                            ) : (
                                <u className="cursor-pointer" onClick={() => !selfPost && showRoundModal((selectedRound + offset), dateMoment, location)} data-testid={testIdPrefix && `${testIdPrefix}.set-location-btn`}>
                                    Set Location
                                </u>
                            )}
                        </p>
                        <p className={`date mb16 ${showRoundAutoFill ? 'left-align' : ''}`} data-testid={testIdPrefix && `${testIdPrefix}.date`}>
                            {isReadOnly ? dateMoment?.format('dddd, MMMM Do') || <>&nbsp;</> : date ? (
                                <>
                                    <u className="cursor-pointer" onClick={() => !selfPost && showRoundModal((selectedRound + offset), dateMoment, location)} data-testid={testIdPrefix && `${testIdPrefix}.set-date-btn`}>
                                        {dateMoment?.format('dddd, MMMM Do')}
                                    </u>
                                    &nbsp;
                                    at
                                    <u className="cursor-pointer" onClick={() => !selfPost && showRoundModal((selectedRound + offset), dateMoment, location)} data-testid={testIdPrefix && `${testIdPrefix}.set-time-btn`}>
                                        &nbsp;
                                        {dateMoment?.format('h:mma')}
                                    </u>
                                </>
                            ) : (
                                <u className="cursor-pointer" onClick={() => !selfPost && showRoundModal((selectedRound + offset), dateMoment, location)} data-testid={testIdPrefix && `${testIdPrefix}.set-date-btn`}>
                                    Set Date & Time
                                </u>
                            )}
                        </p>
                    </div>
                ) : (
                    <div className="name mt24" />
                )}
                {showRoundAutoFill && (
                    <div className="mt32 fs13">
                        <span
                            className="float-right clickable underline"
                            onClick={() => props.showAutofillModal(draw.id, draw.rounds[selectedRound].id)}
                            data-testid={testIdPrefix && `${testIdPrefix}.autofill-btn`}
                        >
                            <i className="uts uts-refresh" /> Auto-Fill
                        </span>
                    </div>
                )}
            </div>
        );
    };

    const handleFacilityChange = (e) => {
        dispatch(drawEditActions.setResetTimer(true));
        setFacilityId(e.target.value);
        setCourtId(''); // reset court if facility is changed
    };

    const handleCourtChange = (e) => {
        dispatch(drawEditActions.setResetTimer(true));
        setCourtId(e.target.value);
    };

    const renderTimeSelect = (selected, changeHandler, testId) => {
        const options = getTimeSelectOptions();

        let time = '';
        if (selected) {
            const selected24Hour = (`0${selected.get('hour')}`).substr(-2, 2);
            const selectedMinutes = (`0${selected.get('minute')}`).substr(-2, 2);
            time = `${selected24Hour}:${selectedMinutes}`;
        }

        return (
            <select
                className={`form-control time-select`}
                onChange={(e) => changeHandler(e.target.value)}
                value={time}
                data-testid={testId}
            >
                <option value="">Time</option>
                {options.map(option => <option value={option.value} key={option.value}>{option.title}</option>)}
            </select>
        );
    };

    const renderLocationFacilityList = () => {
        const facilityList = [];
        (locations.data || []).forEach(loc => {
            loc.facilities.forEach(fac => {
                if (fac.courts.some(c => c.enabled)) {
                    facilityList.push({
                        label: `${loc.name} - ${fac.abbreviation}`,
                        value: fac.id,
                    });
                }
            });
        });
        return _.orderBy(facilityList, [f => f.label?.toLowerCase()]);
    };

    const renderFacilityCourts = () => {
        const courts = [];
        (locations.data || []).forEach(loc => {
            loc.facilities.forEach(fac => {
                if (fac.id === facilityId) {
                    fac.courts.forEach(c => {
                        if (c.enabled) {
                            courts.push(c);
                        }
                    });
                }
            });
        });
        return _.orderBy(courts, [c => c.name?.toLowerCase()]);
    };

    const playerOptionsCol1 = getAvailablePlayersForDraw(selectedRound);
    const playerOptionsCol2 = getAvailablePlayersForDraw(selectedRound + 1);
    const teamOptionsCol1 = getAvailableTeamsForDraw(selectedRound);
    const teamOptionsCol2 = getAvailableTeamsForDraw(selectedRound + 1);

    return (
        <>
            {draw.rounds?.length > 0 && (
                <div className={`col-md-4 draws-round ${getRoundSpacerClassName(draw.eliminationTypeId, draw.rounds, selectedRound, 1)}`} data-round={getRoundSpacerClassName(draw.eliminationTypeId, draw.rounds, selectedRound, 1)}>
                    <div className="rounds-header text-semi text-dark text-center lh36 d-lg-none" data-testid="draw-edit.rounds.col1.mobile-header">{renderRoundLabel(draw.rounds[selectedRound], draw.eliminationTypeId, draw.categoryTypeId)}
                        <div className={`click-area click-area-36 float-left ${selectedRound === 0 ? 'disabled' : ''}`} onClick={() => updateSelectedRound('down')} data-testid="draw-edit.rounds.col1.prev-btn">
                            <i className={`uts uts-prev`} />
                        </div>
                        <div
                            className={`click-area click-area-36 float-right ${disableNextButton() ? 'disabled' : ''}`}
                            onClick={() => {
                                if (!disableNextButton()) {
                                    updateSelectedRound('up', draw.rounds.length);
                                }
                            }}
                            data-testid="draw-edit.rounds.col1.next-btn"
                        >
                            <i className={`uts uts-next`} />
                        </div>
                    </div>
                    {roundDetails(draw, selectedRound, 1, 'draw-edit.rounds.col1')}
                    <div className="draw-cards">
                        {draw?.rounds[selectedRound]?.matches.map((match, mi) => (
                            isSinglesFormat(draw.formatTypeId) ? (
                                <MatchV2
                                    key={`${match.id}-${match.matchPosition}`}
                                    drawId={draw.id}
                                    parentDrawId={parentDraw.id}
                                    drawFormatTypeId={draw.formatTypeId}
                                    drawEliminationTypeId={draw.eliminationTypeId}
                                    roundIndex={selectedRound}
                                    matchIndex={mi}
                                    match={match}
                                    playersDict={playersDict}
                                    playersDictByMemberId={playersDictByMemberId}
                                    membersInDrawDict={membersInDrawDict}
                                    playerOptions={playerOptionsCol1}
                                    playerSelect={addToMatch}
                                    timeZone={timeZone}
                                    utrType={utrType}
                                    handlePostScore={handlePostScore}
                                    isPlayerPostDraw={isPlayerPostDraw}
                                    renderTimeSelect={renderTimeSelect}
                                    matchPositionNumbers={matchPositionNumbers?.[selectedRound][mi]}
                                    teamsDict={teamsDict}
                                    sportTypeId={sportTypeId}
                                    scoringTypeId={draw.scoringTypeId}
                                    isDrawPublished={draw.published}
                                    {...props}
                                />
                            ) : (
                                <MatchV2
                                    key={`${match.id}-${match.matchPosition}`}
                                    drawId={draw.id}
                                    parentDrawId={parentDraw.id}
                                    drawFormatTypeId={draw.formatTypeId}
                                    drawEliminationTypeId={draw.eliminationTypeId}
                                    roundIndex={selectedRound}
                                    matchIndex={mi}
                                    match={match}
                                    playersDict={playersDict}
                                    playersDictByMemberId={playersDictByMemberId}
                                    teamsDict={teamsDict}
                                    teamsInDrawDict={teamsInDrawDict}
                                    playerOptions={teamOptionsCol1}
                                    playerSelect={addToMatch}
                                    timeZone={timeZone}
                                    utrType={utrType}
                                    handlePostScore={handlePostScore}
                                    isPlayerPostDraw={isPlayerPostDraw}
                                    renderTimeSelect={renderTimeSelect}
                                    matchPositionNumbers={matchPositionNumbers?.[selectedRound][mi]}
                                    sportTypeId={sportTypeId}
                                    scoringTypeId={draw.scoringTypeId}
                                    isDrawPublished={draw.published}
                                    {...props}
                                />
                            )
                        ))}
                    </div>
                </div>
            )}
            {draw.rounds?.length > 1 && !isCutOffRound(selectedRound + 1) && (
                <div className={`col-md-4 draws-round ${getRoundSpacerClassName(draw.eliminationTypeId, draw.rounds, selectedRound, 2)}`} data-round={getRoundSpacerClassName(draw.eliminationTypeId, draw.rounds, selectedRound, 2)}>
                    <div className="sub-header text-semi text-dark text-center lh36 d-lg-none" data-testid="draw-edit.rounds.col2.mobile-header">{renderRoundLabel(draw.rounds[selectedRound + 1], draw.eliminationTypeId, draw.categoryTypeId)}</div>
                    {roundDetails(draw, selectedRound + 1, 1, 'draw-edit.rounds.col2')}
                    <div className="draw-cards">
                        {draw.rounds?.[selectedRound + 1]?.matches.map((match, mi) => (
                            isSinglesFormat(draw.formatTypeId) ? (
                                <MatchV2
                                    key={`${match.id}-${match.matchPosition}`}
                                    drawId={draw.id}
                                    parentDrawId={parentDraw.id}
                                    drawFormatTypeId={draw.formatTypeId}
                                    drawEliminationTypeId={draw.eliminationTypeId}
                                    roundIndex={selectedRound + 1}
                                    matchIndex={mi}
                                    match={match}
                                    playersDict={playersDict}
                                    playersDictByMemberId={playersDictByMemberId}
                                    membersInDrawDict={membersInDrawDict}
                                    playerOptions={playerOptionsCol2}
                                    playerSelect={addToMatch}
                                    timeZone={timeZone}
                                    utrType={utrType}
                                    handlePostScore={handlePostScore}
                                    isPlayerPostDraw={isPlayerPostDraw}
                                    renderTimeSelect={renderTimeSelect}
                                    matchPositionNumbers={matchPositionNumbers?.[selectedRound + 1][mi]}
                                    teamsDict={teamsDict}
                                    sportTypeId={sportTypeId}
                                    scoringTypeId={draw.scoringTypeId}
                                    isDrawPublished={draw.published}
                                    {...props}
                                />
                            ) : (
                                <MatchV2
                                    key={`${match.id}-${match.matchPosition}`}
                                    drawId={draw.id}
                                    parentDrawId={parentDraw.id}
                                    drawFormatTypeId={draw.formatTypeId}
                                    drawEliminationTypeId={draw.eliminationTypeId}
                                    roundIndex={selectedRound + 1}
                                    matchIndex={mi}
                                    match={match}
                                    playersDict={playersDict}
                                    playersDictByMemberId={playersDictByMemberId}
                                    teamsDict={teamsDict}
                                    teamsInDrawDict={teamsInDrawDict}
                                    playerOptions={teamOptionsCol2}
                                    playerSelect={addToMatch}
                                    timeZone={timeZone}
                                    utrType={utrType}
                                    handlePostScore={handlePostScore}
                                    isPlayerPostDraw={isPlayerPostDraw}
                                    renderTimeSelect={renderTimeSelect}
                                    matchPositionNumbers={matchPositionNumbers?.[selectedRound + 1][mi]}
                                    sportTypeId={sportTypeId}
                                    scoringTypeId={draw.scoringTypeId}
                                    isDrawPublished={draw.published}
                                    {...props}
                                />
                            )
                        ))}
                    </div>
                </div>
            )}
            {showModal && (
                <Modal visible={showModal} onClickBackdrop={closeModals}>
                    <div className="modal-header">
                        <div className="text-medium" data-testid="modal.draw-edit.change-round.title">Change Round #{selectedRoundNumber}</div>
                        <div className="close-icon" onClick={closeModals} data-testid="modal.draw-edit.change-round.close-btn"><i className="utr utr-close" /></div>
                    </div>
                    <div className="modal-body">
                        {selectedRoundMoment && (
                            <div className={`form-group fo  rm-group-xl`}>
                                <label>Default Date & Time</label>
                                <div className="row">
                                    <div className="col-7 pr4">
                                        <div className="input-wrapper">
                                            <Datepicker
                                                className="form-control"
                                                selected={moment(selectedRoundMoment)}
                                                onChange={handleRoundDateChange}
                                                minDate={eventStartLocal}
                                                maxDate={eventEndLocal}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-5 pl0">
                                        <div className="input-wrapper">
                                            {renderTimeSelect(selectedRoundMoment, handleRoundTimeChange, 'modal.draw-edit.change-round.time-select')}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        {scheduleTypeId === drawScheduleTypeIds.MATCH_SHEET ? (
                            <div className={`form-group form-group-xl`}>
                                <label>Location</label>
                                <div className="row">
                                    <div className="col-6 pr4">
                                        <div className="input-wrapper">
                                            <select className="form-control" value={facilityId} onChange={handleFacilityChange} data-testid="modal.draw-edit.change-round.facility-select">
                                                <option value={''}>{locations.loading ? 'Loading...' : 'Choose facility...'}</option>
                                                {renderLocationFacilityList().map(fac => (
                                                    <option key={`facility-${fac.value}`} value={fac.value}>{fac.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-6 pl0">
                                        <div className="input-wrapper">
                                            <select className="form-control" value={courtId} onChange={handleCourtChange} data-testid="modal.draw-edit.change-round.court-select">
                                                <option value={''}>{locations.loading ? 'Loading...' : 'Choose court...'}</option>
                                                {renderFacilityCourts().map(c => (
                                                    <option key={`court-${c.id}`} value={c.id}>{c.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className={`form-group form-group-xl`}>
                                    <label>Default Location</label>
                                    <div className="input-wrapper">
                                        <input type="text" className="form-control" value={location} onChange={handleRoundLocationChange} data-testid="modal.draw-edit.change-round.location-input" />
                                    </div>
                                </div>
                                <div className={`checkbox checkbox-large mb24 ${propagate ? 'selected' : ''}`} onClick={() => toggleRoundPropagation()} data-testid="modal.draw-edit.change-round.overwrite-check">
                                    <div className="checkbox-wrapper">
                                        <div className={`checkbox-check`}><Img src="/img/icons/checkmark-icon.svg" /></div>
                                    </div>
                                    <div className="label">
                                        <div className="text text-small" data-testid="modal.draw-edit.change-round.overwrite-check.label">Overwrite all existing date and location in Round #{selectedRoundNumber}</div>
                                    </div>
                                </div>
                            </>
                        )}
                        <button type="button" className={`btn btn-xl btn-block btn-primary`} onClick={updateRoundDateTime} data-testid="modal.draw-edit.change-round.ok-btn">OK</button>
                    </div>
                </Modal>
            )}
        </>
    );
};

RoundsV2.propTypes = {
    isReadOnly: PropTypes.bool.isRequired,
    draw: PropTypes.objectOf(any).isRequired,
    parentDraw: PropTypes.objectOf(any).isRequired,
    selectedRound: PropTypes.number.isRequired,
    updateSelectedRound: PropTypes.func.isRequired,
    disableNextButton: PropTypes.bool.isRequired,
    playersDict: PropTypes.objectOf(any).isRequired,
    playersDictByMemberId: PropTypes.objectOf(any).isRequired,
    timeZone: PropTypes.string.isRequired,
    utrType: PropTypes.string.isRequired,
    teamsDict: PropTypes.objectOf(any).isRequired,
    isCutOffRound: PropTypes.bool.isRequired,
    addToMatch: PropTypes.func.isRequired,
    handlePostScore: PropTypes.func.isRequired,
    isPTTEvent: PropTypes.bool.isRequired,
    membersInDrawDict: PropTypes.objectOf(any).isRequired,
    teamsInDrawDict: PropTypes.objectOf(any).isRequired,
    getAvailablePlayersForDraw: PropTypes.func.isRequired,
    getAvailableTeamsForDraw: PropTypes.func.isRequired,
    sportTypeId: PropTypes.number.isRequired,
};

export default React.memo(RoundsV2);
